'use client';

import React from 'react';

import HeartOutline from '@/assets/icons/heart-outline.svg';
import HeartFilled from '@/assets/icons/heart-filled.svg';
import { FavouriteType, useFavourites } from '@/context/FavouritesContext';
import {
  useIsMPLivliFamily,
  useTenantConfig,
} from '@/providers/ConfigProvider';
import { useUser } from '@/providers/UserProvider';
import { isBrowser } from '@/helpers/is-browser';

interface FavouriteButtonProps {
  id: string;
  type: FavouriteType;
  className?: string;
  iconclass?: string;
  name: string;
}

export const FavouriteButton = ({
  id,
  type,
  className = '',
  iconclass = '',
  name,
}: FavouriteButtonProps) => {
  const isLivliFamily = useIsMPLivliFamily();
  const { isFavourite, toggleFavourite } = useFavourites();
  const { user } = useUser();
  const favourite = isFavourite(id);
  const config = useTenantConfig();
  const showFavourite =
    config?.NEXT_PUBLIC_ICIM_LOGIN_ENABLED === 'true' &&
    config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true';

  if (!showFavourite) return null;

  const sendEventToDataLayer = () => {
    if (isBrowser && dataLayer && !favourite && user) {
      dataLayer.push({
        event: 'my_favourites_added',
        content_type: type,
        contentful_id: id,
        name: name || '',
        user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
      });
    }
  };

  return (
    <button
      type="button"
      onClick={() => {
        toggleFavourite(id, type, name);
        sendEventToDataLayer();
      }}
      aria-label={favourite ? 'Remove from favourites' : 'Add to favourites'}
      aria-pressed={favourite}
      className={`grid h-10 w-10 place-items-center rounded-full bg-brand-white outline-0 transition-all lg:hover:bg-neutral-grey-100 focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-3 focus-visible:outline-icon-dark active:bg-neutral-grey-200 ${className}`}
    >
      {favourite ? (
        <HeartFilled
          viewBox="0 0 30 28"
          className={`h-5 w-5 transition-all ${iconclass}`}
        />
      ) : (
        <HeartOutline
          viewBox="0 0 22 20"
          className={`h-5 w-5 transition-all ${iconclass}`}
        />
      )}
    </button>
  );
};

export default FavouriteButton;
